import { useState } from 'react';
import { leads, leadScoreConfig, leadStatuses, type Lead, type LeadScore, type LeadStatus } from '../../../mocks/leads';

interface LeadsTableProps {
  onSelectLead: (lead: Lead) => void;
}

function ScoreBadge({ score }: { score: LeadScore }) {
  const cfg = leadScoreConfig[score];
  const icons = { hot: 'ri-fire-line', warm: 'ri-sun-line', cold: 'ri-snowflake-line' };
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap" style={{ backgroundColor: cfg.bg, color: cfg.color }}>
      <i className={`${icons[score]} text-[11px]`} />
      {cfg.label}
    </span>
  );
}

function StatusBadge({ status }: { status: LeadStatus }) {
  const cfg = leadStatuses.find(s => s.value === status);
  if (!cfg) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap" style={{ backgroundColor: `${cfg.color}18`, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

export default function LeadsTable({ onSelectLead }: LeadsTableProps) {
  const [searchVal, setSearchVal] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterScore, setFilterScore] = useState('all');

  const filtered = leads.filter(l => {
    if (filterStatus !== 'all' && l.status !== filterStatus) return false;
    if (filterSource !== 'all' && l.source !== filterSource) return false;
    if (filterScore !== 'all' && l.score !== filterScore) return false;
    if (searchVal && !l.name.toLowerCase().includes(searchVal.toLowerCase()) && !l.phone.includes(searchVal)) return false;
    return true;
  });

  return (
    <>
      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-card p-5">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <div className="w-4 h-4 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2">
              <i className="ri-search-line text-charcoal-300 text-[14px]" />
            </div>
            <input value={searchVal} onChange={e => setSearchVal(e.target.value)} placeholder="İsim veya telefon ara..." className="w-full h-[38px] pl-9 pr-4 bg-[#FAF8F4] border border-border rounded-xl text-[13px] placeholder-charcoal-300 focus:outline-none focus:border-champagne-400" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="h-[38px] px-3 bg-[#FAF8F4] border border-border rounded-xl text-[13px] text-charcoal-500 focus:outline-none focus:border-champagne-400 cursor-pointer">
            <option value="all">Tüm Durumlar</option>
            {leadStatuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={filterSource} onChange={e => setFilterSource(e.target.value)} className="h-[38px] px-3 bg-[#FAF8F4] border border-border rounded-xl text-[13px] text-charcoal-500 focus:outline-none focus:border-champagne-400 cursor-pointer">
            <option value="all">Tüm Kaynaklar</option>
            <option value="google">Google</option>
            <option value="instagram">Instagram</option>
            <option value="facebook">Facebook</option>
            <option value="organic">Organik</option>
            <option value="tiktok">TikTok</option>
          </select>
          <select value={filterScore} onChange={e => setFilterScore(e.target.value)} className="h-[38px] px-3 bg-[#FAF8F4] border border-border rounded-xl text-[13px] text-charcoal-500 focus:outline-none focus:border-champagne-400 cursor-pointer">
            <option value="all">Tüm Skorlar</option>
            <option value="hot">Sıcak</option>
            <option value="warm">Ilık</option>
            <option value="cold">Soğuk</option>
          </select>
          <button className="h-[38px] px-4 bg-[#FAF8F4] border border-border rounded-xl text-[13px] text-charcoal-400 hover:border-champagne-400 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line text-champagne-500 text-[14px]" /></div>
            Tarih Filtrele
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-[15px] font-semibold text-charcoal-600">Lead Listesi</h3>
            <p className="text-[12px] text-charcoal-300 mt-0.5">{filtered.length} lead bulundu</p>
          </div>
          <button className="flex items-center gap-2 h-[34px] px-4 bg-champagne-500 text-white rounded-xl text-[12px] font-medium hover:bg-champagne-600 transition-colors cursor-pointer whitespace-nowrap">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-download-line text-[13px]" /></div>
            Dışa Aktar
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#FAF8F4] border-b border-border">
                {['Lead', 'Telefon', 'Kampanya', 'Kaynak', 'Durum', 'Skor', 'Oluşturulma', 'Son Etkileşim', 'Etiketler', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-charcoal-300 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((lead, idx) => (
                <tr key={lead.id} className={`border-b border-border hover:bg-[#FAF8F4] transition-colors cursor-pointer ${idx % 2 === 0 ? '' : 'bg-[#FDFCFA]'}`} onClick={() => onSelectLead(lead)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <img src={lead.avatar} alt={lead.name} className="w-8 h-8 rounded-full object-top object-cover flex-shrink-0" />
                      <div>
                        <p className="text-[13px] font-semibold text-charcoal-600 whitespace-nowrap">{lead.name}</p>
                        <p className="text-[11px] text-charcoal-300">{lead.city}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[12px] text-charcoal-400 font-mono whitespace-nowrap">{lead.phone}</td>
                  <td className="px-4 py-3 text-[12px] text-charcoal-500 max-w-[160px] truncate">{lead.campaign}</td>
                  <td className="px-4 py-3 text-[12px] text-charcoal-400 capitalize">{lead.source}</td>
                  <td className="px-4 py-3"><StatusBadge status={lead.status} /></td>
                  <td className="px-4 py-3"><ScoreBadge score={lead.score} /></td>
                  <td className="px-4 py-3 text-[12px] text-charcoal-400 whitespace-nowrap">{lead.createdAt}</td>
                  <td className="px-4 py-3 text-[12px] text-charcoal-300 whitespace-nowrap">{lead.lastInteraction}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-[140px]">
                      {lead.tags.slice(0, 2).map(tag => (
                        <span key={tag} className="px-2 py-0.5 bg-champagne-50 text-champagne-700 rounded-full text-[10px] font-medium whitespace-nowrap">{tag}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-champagne-50 text-charcoal-300 hover:text-champagne-600 transition-colors cursor-pointer" title="Düzenle">
                        <i className="ri-edit-line text-[13px]" />
                      </button>
                      <button className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-charcoal-300 hover:text-red-400 transition-colors cursor-pointer" title="Arşivle">
                        <i className="ri-archive-line text-[13px]" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
